import { useCallback, useState, useEffect } from "react"
import { Form, FormFeedback, Label, Button, Row, Col } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useCreateVehicleImporter } from "hooks/useVehicleImporter"
import { createVehicleImporterBankAccount } from "api/vehicleImporterBankAccountApi"

const ImporterOnboardingModal = ({
  modal,
  toggleModal,
  onSuccess,
  prefillImporter = null,
}) => {
  const { mutateAsync: createImporter, isPending: createLoading } = useCreateVehicleImporter()
  const [addBankAccount, setAddBankAccount] = useState(false)

  useEffect(() => {
    if (modal) {
      setAddBankAccount(!!prefillImporter)
    }
  }, [modal, prefillImporter])

  const validationSchema = Yup.object({
    name: prefillImporter
      ? Yup.string().nullable()
      : Yup.string().trim().required("Name is required"),
    mobile: Yup.string().nullable(),
    email: Yup.string().email("Invalid email").nullable(),
    ba_label: Yup.string().nullable(),
    ba_account_name: Yup.string().when([], {
      is: () => addBankAccount,
      then: schema => schema.trim().required("Account name is required"),
      otherwise: schema => schema.nullable(),
    }),
    ba_account_number: Yup.string().when([], {
      is: () => addBankAccount,
      then: schema => schema.trim().required("Account number is required"),
      otherwise: schema => schema.nullable(),
    }),
    ba_account_mobile_number: Yup.string().max(20, "Max 20 characters").nullable(),
    ba_bank_name: Yup.string().when([], {
      is: () => addBankAccount,
      then: schema => schema.trim().required("Bank name is required"),
      otherwise: schema => schema.nullable(),
    }),
    ba_branch_name: Yup.string().nullable(),
    ba_routing_number: Yup.string().nullable(),
    ba_is_default: Yup.boolean(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        let newImporter = prefillImporter

        if (!prefillImporter) {
          const importerPayload = {
            name: values.name.trim(),
            email: values.email?.trim() || null,
            mobile: values.mobile?.trim() ? [values.mobile.trim()] : [],
          }
          const importerRes = await createImporter(importerPayload)
          newImporter = importerRes?.data
        }

        let createdBankAccount = null
        if (addBankAccount && newImporter?.id) {
          const bankRes = await createVehicleImporterBankAccount({
            importerId: newImporter.id,
            label: values.ba_label?.trim() || null,
            account_name: values.ba_account_name?.trim(),
            account_number: values.ba_account_number?.trim(),
            account_mobile_number: values.ba_account_mobile_number?.trim() || null,
            bank_name: values.ba_bank_name?.trim(),
            branch_name: values.ba_branch_name?.trim() || null,
            routing_number: values.ba_routing_number?.trim() || null,
            is_default: values.ba_is_default,
            is_active: true,
          })
          createdBankAccount = bankRes?.data
          toast.success(
            prefillImporter
              ? "Bank account added successfully."
              : "Importer and bank account created successfully."
          )
        } else {
          toast.success("Importer created successfully.")
        }

        resetForm()
        setAddBankAccount(false)
        onSuccess?.(newImporter, createdBankAccount)
        toggleModal()
      } catch (error) {
        toast.error(error.message || "Failed to save.")
      }
    },
    [createImporter, addBankAccount, prefillImporter, onSuccess, toggleModal]
  )

  const formik = useFormik({
    initialValues: {
      name: "",
      mobile: "",
      email: "",
      ba_label: "",
      ba_account_name: "",
      ba_account_number: "",
      ba_account_mobile_number: "",
      ba_bank_name: "",
      ba_branch_name: "",
      ba_routing_number: "",
      ba_is_default: true,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const handleClose = () => {
    formik.resetForm()
    setAddBankAccount(false)
    toggleModal()
  }

  const field = (key, label, placeholder = "", required = false) => (
    <div className="mb-3">
      <Label htmlFor={key}>
        {label}
        {required && <span className="text-danger"> *</span>}
      </Label>
      <InputField
        name={key}
        id={key}
        value={formik.values[key]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        invalid={formik.touched[key] && !!formik.errors[key]}
        placeholder={placeholder}
      />
      {formik.touched[key] && formik.errors[key] && (
        <FormFeedback type="invalid">{formik.errors[key]}</FormFeedback>
      )}
    </div>
  )

  const modalTitle = prefillImporter
    ? `Add Bank Account — ${prefillImporter.name}`
    : "Importer Onboarding"

  return (
    <ThemeModal
      isOpen={modal}
      toggle={handleClose}
      title={modalTitle}
      size="lg"
      footer={
        <div className="d-flex gap-2 justify-content-end w-100">
          <Button type="button" color="secondary" onClick={handleClose} disabled={createLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="success"
            form="importer-onboarding-form"
            disabled={createLoading}
          >
            {createLoading
              ? "Saving..."
              : prefillImporter
              ? "Add Bank Account"
              : "Create Importer"}
          </Button>
        </div>
      }
    >
      <Form id="importer-onboarding-form" onSubmit={formik.handleSubmit}>
        {/* Importer Info — hidden when prefillImporter is provided */}
        {!prefillImporter && (
          <div className="mb-3 pb-2 border-bottom">
            <h6 className="text-muted mb-3">Importer Information</h6>
            <Row>
              <Col md={12}>{field("name", "Name", "Importer name", true)}</Col>
              <Col md={6}>{field("mobile", "Mobile", "Mobile number")}</Col>
              <Col md={6}>{field("email", "Email", "Email address")}</Col>
            </Row>
          </div>
        )}

        {/* Bank Account Section */}
        <div>
          {!prefillImporter && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-muted mb-0">Bank Account</h6>
              <Button
                type="button"
                color={addBankAccount ? "danger" : "primary"}
                outline
                size="sm"
                onClick={() => setAddBankAccount(prev => !prev)}
              >
                {addBankAccount ? "Remove Bank Account" : "+ Add Bank Account"}
              </Button>
            </div>
          )}

          {addBankAccount && (
            <Row>
              <Col md={6}>{field("ba_label", "Label", "e.g. Head Office")}</Col>
              <Col md={6}>{field("ba_account_name", "Account Name", "", true)}</Col>
              <Col md={6}>{field("ba_account_number", "Account Number", "", true)}</Col>
              <Col md={6}>{field("ba_account_mobile_number", "Account Mobile", "")}</Col>
              <Col md={6}>{field("ba_bank_name", "Bank Name", "", true)}</Col>
              <Col md={6}>{field("ba_branch_name", "Branch Name", "")}</Col>
              <Col md={6}>{field("ba_routing_number", "Routing Number", "")}</Col>
              <Col md={6} className="d-flex align-items-center pt-1">
                <Button
                  type="button"
                  color={formik.values.ba_is_default ? "success" : "light"}
                  outline={!formik.values.ba_is_default}
                  size="sm"
                  onClick={() =>
                    formik.setFieldValue("ba_is_default", !formik.values.ba_is_default)
                  }
                >
                  <i
                    className={`bx ${
                      formik.values.ba_is_default ? "bx-check-double" : "bx-circle"
                    } me-1`}
                  />
                  {formik.values.ba_is_default ? "Default Account" : "Mark as Default"}
                </Button>
              </Col>
            </Row>
          )}
        </div>
      </Form>
    </ThemeModal>
  )
}

export default ImporterOnboardingModal
